import {RefObject} from 'react'
import {Nullable} from '../../../app/TypeHelpers'
import {HKConstants} from '../../../HKConstants'
import {SessionStorageHelper} from '../../../SessionStorageHelper'
import {AsyncFunction} from '../../../components/shared/hoc/withAsyncCommunication'
import {HttpError} from '../../../network/errors/HttpError'
import {MaintenanceError} from '../../../network/errors/MaintenanceError'
import {BaseHttpError} from '../../../network/errors/baseHandling/BaseHttpError'
import {Messages} from '../../../Messages'
import {
    CodeMasterInterface,
    EVENT_REPEAT_DAY,
    EVENT_REPEAT_DAY_OF_THE_WEEK,
    EVENT_REPEAT_UNIT_FOR_WEEK,
    EVENT_REPEAT_WEEK,
    HOURS,
    MESSAGE_TYPE,
    MINUTS,
    PREFECTURES_MASTER,
    ROLE_FOR_ADMINISTRATOR,
    ROLE_TYPE,
    USER_DISPLAY_ITEMS,
} from '../../../CodeMaster'
import {SessionTimeOutError} from '../../../network/errors/SessionTimeOutError'
import {Informations} from '../../components/common/repository/NatBaseResponseInterface'
import {Warning} from '../../components/common/admin/NatAcceptedWarning'
import {HttpCritical} from '../../../network/errors/baseHandling/HttpCritical'
import {NavigateFunction} from 'react-router-dom'
import {isKana, isKanji, toKatakana} from 'wanakana'

export function refValue(ref: RefObject<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): string {
    if (ref.current === null) {
        return ''
    }
    return ref.current.value
}

export function refChecked(ref: RefObject<HTMLInputElement>): boolean {
    if (ref.current === null) {
        return false
    }
    return ref.current.checked
}

export function getQueryParameter(paramKey: string, url: string): string {
    let query: string = ''
    paramKey = paramKey.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]')
    const regex: any = new RegExp('[\\?&]' + paramKey + '=([^&#]*)')
    const results: any = regex.exec(url)
    if (results) {
        query = decodeURIComponent(results[1].replace(/\+/g, ' '))
    }
    return query
}

export function getQueryParameters(paramKey: string, location: Location): string[] {
    return new URL(location.href).searchParams.getAll(paramKey)
}

export function getCurrentYear(currentDate: string): number {
    const now: Date = new Date(currentDate)
    return now.getFullYear()
}

export function getCurrentMonth(currentDate: string): number {
    const now: Date = new Date(currentDate)
    return now.getMonth() + 1
}

export function getCurrentDay(currentDate: string): number {
    const now: Date = new Date(currentDate)
    return now.getDate()
}

export function getCurrentDayOfWeek(currentDate: string): number {
    const now: Date = new Date(currentDate)
    return now.getDay()	// 曜日(数値)
}

export function getCurrentDayOfWeekStr(currentDate: string): string {
    const now: Date = new Date(currentDate)
    const dayOfWeek: number = now.getDay()	// 曜日(数値)
    return ['日', '月', '火', '水', '木', '金', '土'][dayOfWeek]
}

export function isNull(str: string): Nullable<string> {
    if (str !== '' && str.replace(/\s+/g, '').length !== 0) {
        return str
    } else {
        return null
    }
}

export function getStringOrEmptyString(str: string | null): string {
    if (str) {
        return str
    } else {
        return ''
    }
}

export function judgeAuthorityCheck(authority: string): boolean {
    const authorities: string[] = JSON.parse(SessionStorageHelper.getStringItemWithDefault('AUTHORITIES', '[]'))
    return authorities.includes(authority)
}

export function judgeHaveAnyOneOfAuthorities(...authorities: string[]): boolean {
    const loginUserAuthorities: string[] = JSON.parse(
        SessionStorageHelper.getStringItemWithDefault('AUTHORITIES', '[]'),
    )
    return authorities.some((authority: string) => loginUserAuthorities.includes(authority))
}

export function judgeRoleCheck(role: string[]): boolean {
    const loginRole: Nullable<string> = SessionStorageHelper.getStringItemOrNull('loginUserRoleId')
    if (loginRole) {
        return role.includes(loginRole)
    }
    return false
}

// チケット#2558の資料に従い、ログインユーザーに対しメールアドレスなどをマスクする必要があるか判断する
// なお、管理組合役員に対しては居住者ユーザ参照権限を付与できないようにすることで対処しているため、この関数では考慮しない。
// サーバのAuthorityCheckUtil#needsMaskConfidentialDataByLoginUserが同じロジックの処理になっている。
export function needsMaskConfidentialDataByLoginUser(): boolean {
    if (judgeAuthorityCheck(HKConstants.CMN.AUTH.EDIT_RESIDENT_USER)) {
        return false
    }
    const maskRoles = [
        ROLE_FOR_ADMINISTRATOR.LIFE_MANAGER.value,
        ROLE_FOR_ADMINISTRATOR.CONCIERGE.value,
    ]
    return judgeRoleCheck(maskRoles)
}

export function getYearDate(): string {
    return String(HKConstants.VMN.NTF.MIN_SELECTABLE_YEAR) + '-01-01'
}

export function getYearAfterNextDate(currentDate: string): string {

    const date: Date = new Date(currentDate)
    const month: number = date.getMonth() + 1
    let year: number = date.getFullYear()

    if (month > HKConstants.VMN.NTF.MAX_SELECTABLE_MONTH) {
        year = year + 1
    }

    const afterNextYear: number = year + HKConstants.VMN.NTF.MAX_SELECTABLE_ADD_YEAR
    const afterNextMonth: number = HKConstants.VMN.NTF.MAX_SELECTABLE_MONTH
    const afterNextDay: Date = new Date(afterNextYear, afterNextMonth, 0)
    const afterNextDate: number = afterNextDay.getDate()

    return new Date(
        String(`${year + HKConstants.VMN.NTF.MAX_SELECTABLE_ADD_YEAR}/${HKConstants.VMN.NTF.MAX_SELECTABLE_MONTH}/${afterNextDate}`),
    ).toString()
}

export function getYearDateFac(): string {
    return String(HKConstants.VMN.FAC.MIN_SELECTABLE_YEAR) + '-01-01'
}

export function getYearAfterNextDateFac(currentDate: string): string {

    const date: Date = new Date(currentDate)
    const month: number = date.getMonth() + 1
    let year: number = date.getFullYear()

    if (month > HKConstants.VMN.FAC.MAX_SELECTABLE_MONTH) {
        year = year + 1
    }

    const afterNextYear: number = year + HKConstants.VMN.FAC.MAX_SELECTABLE_ADD_YEAR
    const afterNextMonth: number = HKConstants.VMN.FAC.MAX_SELECTABLE_MONTH
    const afterNextDay: Date = new Date(afterNextYear, afterNextMonth, 0)
    const afterNextDate: number = afterNextDay.getDate()

    return new Date(
        String(`${year + HKConstants.VMN.FAC.MAX_SELECTABLE_ADD_YEAR}/${HKConstants.VMN.FAC.MAX_SELECTABLE_MONTH}/${afterNextDate}`),
    ).toString()
}


export async function NatAsyncCallFunction<R, A>(
    asyncFunction: AsyncFunction<A, R>,
    navigate: NavigateFunction,
    httpCriticalHandler?: (httpCritical: HttpCritical) => void,
): Promise<Nullable<R>> {
    try {
        return await asyncFunction()
    } catch (requestError) {
        if (requestError instanceof HttpError) {
            throw requestError
        } else if (requestError instanceof MaintenanceError) {
            if (window.name !== 'maintenance') {
                window.location.reload()
                window.name = 'maintenance'
                return null
            } else {
                window.name = ''
                return null
            }
        } else if (requestError instanceof SessionTimeOutError) {
            window.location.href = `/resident/login?sessionTimeOut=1`
            return null
        } else {
            const status = (requestError as BaseHttpError).status
            const message = getErrorMessageForHttpStatus(status)

            if (httpCriticalHandler) {
                httpCriticalHandler(requestError as HttpCritical)
                return null
            }

            navigate(
                `/resident/error`,
                {state: {message: message}},
            )
            return null
        }
        return null
    }
}

export function getErrorMessageForHttpStatus(status: number): string {
    let message
    switch (status) {
        case 403:
            message = Messages.ESYCC0025.toString()
            break
        case 404:
            message = Messages.ESYCC0017.toString()
            break
        case 500:
            message = Messages.ESYCC0018.toString()
            break
        case 502:
            message = Messages.ESYCC0019.toString()
            break
        default:
            message = Messages.ESYCC0015.toString()
    }
    return message
}

export function getScheduleSettingInfo(
    repeatDate: string | null, repeatWeekUnit: string | null,
    repeatWeek: number, repeatDayOfWeek: number): string {
    let scheduleSetting: string = ''

    if (!repeatWeekUnit && !repeatDate) {
        // 『繰り返し週単位』がnull かつ 『繰り返し日』がnull
        scheduleSetting = `単日`
        return scheduleSetting
    }

    if (repeatDate) {
        // 『繰り返し日指定』が存在する場合
        let repeatDateLabel: string = ''
        const repeatDateMaster =
            EVENT_REPEAT_DAY.codeToEnumConstants(repeatDate)
        if (repeatDateMaster && repeatDateMaster.label) {
            repeatDateLabel = repeatDateMaster.label
        }
        scheduleSetting = `毎月　${repeatDateLabel}`
        if (EVENT_REPEAT_DAY.END_OF_MONTH.value !== repeatDate) {
            scheduleSetting = scheduleSetting + '日'
        }
        return scheduleSetting
    }

    if (repeatWeekUnit) {
        // 繰り返し週単位
        const repeatWeekUnitMaster =
            EVENT_REPEAT_UNIT_FOR_WEEK.codeToEnumConstants(repeatWeekUnit)
        if (repeatWeekUnitMaster && repeatWeekUnitMaster.value !==
            EVENT_REPEAT_UNIT_FOR_WEEK.SELECTION_WEEK.value) {
            // 繰り返し週単位 = 週選択　以外の場合、文言に追加
            scheduleSetting = repeatWeekUnitMaster.label
        }

        // 週選択
        if (repeatWeekUnit === EVENT_REPEAT_UNIT_FOR_WEEK.SELECTION_WEEK.value) {
            // 繰り返し週
            const tmpRepeatWeek: CodeMasterInterface[] = []
            const sortRepeatWeek: CodeMasterInterface[] = EVENT_REPEAT_WEEK.values()
            sortRepeatWeek.sort((a, b) => Number(b.value) - Number(a.value))

            sortRepeatWeek.map((week) => {
                if (repeatWeek >= Number(week.value)) {
                    tmpRepeatWeek.push(week)
                    repeatWeek -= Number(week.value)
                }
            })
            tmpRepeatWeek.sort((a, b) => Number(a.displayOrder) - Number(b.displayOrder))
            const DispRepeatWeek: string[] = []
            tmpRepeatWeek.map((week) => {
                DispRepeatWeek.push(week.label)
            })
            const repeatWeekText: string = DispRepeatWeek.join('・')
            scheduleSetting += repeatWeekText
        }

        // イベント繰り返し曜日
        const tmpRepeatDayOfTheWeek: CodeMasterInterface[] = []
        const sortRepeatDayOfTheWeek: CodeMasterInterface[] = EVENT_REPEAT_DAY_OF_THE_WEEK.values()
        sortRepeatDayOfTheWeek.sort((a, b) => Number(b.value) - Number(a.value))
        sortRepeatDayOfTheWeek.map((dayOfTheWeek) => {
            if (repeatDayOfWeek >= Number(dayOfTheWeek.value)) {
                tmpRepeatDayOfTheWeek.push(dayOfTheWeek)
                repeatDayOfWeek -= Number(dayOfTheWeek.value)
            }
        })
        tmpRepeatDayOfTheWeek.sort((a, b) => Number(a.displayOrder) - Number(b.displayOrder))
        const repeatDayOfTheWeek: string[] = []
        tmpRepeatDayOfTheWeek.map((dayOfTheWeek) => {
            repeatDayOfTheWeek.push(dayOfTheWeek.label)
        })
        const repeatDayOfTheWeekText: string = repeatDayOfTheWeek.join('・')
        scheduleSetting += `　${repeatDayOfTheWeekText}`

        return scheduleSetting
    }
    return scheduleSetting
}

export function nullToBlank(str: Nullable<string>): string {
    if (str === null) {
        return ''
    }
    return str
}

export function getHourLabel(hourValue: string): string {
    const hourLabel = HOURS.codeToEnumConstants(String(Number(hourValue)))
    if (hourLabel) {
        return hourLabel.label
    } else {
        return ''
    }
}

export function getMinutsLabel(minutsValue: string): string {
    const minutsLabel = MINUTS.codeToEnumConstants(String(Number(minutsValue)))
    if (minutsLabel) {
        return minutsLabel.label
    } else {
        return ''
    }
}

export function getAcceptedWarningList(informations: Informations[]): Warning[] {
    return informations.filter((tmpInformation) => tmpInformation.type === MESSAGE_TYPE.WARNING.value)
        .map((information) => {
            return ({
                messageId: `${information.messageId}`,
                message: `${information.message}`,
            })
        })
}

export function setHighlightingWarningList(highlightingMessegIdList: string[], warningList: Warning[]): Warning[] {
    const highlightingWarningList: Warning[] = []
    for (const warning of warningList) {
        highlightingWarningList.push({
                messageId: warning.messageId,
                message: warning.message,
                highlighting: highlightingMessegIdList.includes(warning.messageId),
            },
        )
    }
    return highlightingWarningList
}

export function convertToRoomNumberForResident(roomNumber: Nullable<string>): Nullable<string> {
    if (!roomNumber) {
        return roomNumber
    }

    if (roomNumber.match(/^0+\d{3}/)) {
        return roomNumber.slice(1)
    }

    return roomNumber
}

export function combineRidgeNameAndRoomNumber(
    ridgeName: Nullable<string>,
    roomNumber: Nullable<string>
): Nullable<string> {
    const convertToRoomNumber = convertToRoomNumberForResident(roomNumber ?? '')
    return ridgeName ? ridgeName + ' ' + convertToRoomNumber : convertToRoomNumber
}

export function reverseConvertToRoomNumberForResident(roomNumber: Nullable<string>): string {
    if (!roomNumber) {
        return ''
    }

    return (`0000${roomNumber}`).slice(-4)
}


export function openPdfPreviewWindow(data: string): void {
    const beforeElement = document.getElementById('preview')
    if (beforeElement) {
        document.body.removeChild(beforeElement)
    }
    const element: HTMLInputElement = document.createElement('input')
    element.type = 'hidden'
    element.name = 'preview'
    element.id = 'preview'
    element.value = data
    document.body.appendChild(element)
    window.open(`/resources/preview/preview.html`, '_blank')
}

export function getFileIconPath(fileNameExtension: string): string {
    let fileIconPath = ''
    switch (fileNameExtension.toUpperCase()) {
        case 'DOCX':
            fileIconPath = `/resources/images/icons/icon_docx.png`
            break
        case 'XLSX':
            fileIconPath = `/resources/images/icons/icon_xlsx.png`
            break
        case 'PPTX':
            fileIconPath = `/resources/images/icons/icon_pptx.png`
            break
        case 'PDF':
            fileIconPath = `/resources/images/icons/icon_pdf.png`
            break
        default:
            fileIconPath = `/resources/images/icons/blank.png`
            break
    }
    return fileIconPath
}


export function getFileExtension(filename: string): string {
    const periodPosition = filename.lastIndexOf('.')
    if (periodPosition === -1) return ''
    return filename.slice(periodPosition + 1)
}

export function getName(filename: string): string {
    const periodPosition = filename.lastIndexOf('.');
    if (periodPosition === -1) return filename;
    return filename.slice(0, periodPosition);
}

export function getThreadContributorLabel(contributorRidgeName: string,
                                          contributorRoomNumber: string,
                                          contributorFamilyName: string,
                                          contributorFirstName: string,
                                          contributorRoleName: string): string {

    if (contributorRoleName) {
        return contributorRoleName
    } else if (!contributorRidgeName && !contributorRoomNumber && !contributorFamilyName && !contributorFirstName) {
        return '匿名 さん'
    } else {
        const contributorInfo: string[] = []

        const convertContributorRoomNumber = convertToRoomNumberForResident(contributorRoomNumber)

        if (contributorRidgeName) {
            contributorInfo.push(contributorRidgeName)
        }
        if (convertContributorRoomNumber) {
            contributorInfo.push(convertContributorRoomNumber)
        }
        if (contributorFamilyName) {
            contributorInfo.push(contributorFamilyName)
        }
        if (contributorFirstName) {
            contributorInfo.push(contributorFirstName)
        }
        contributorInfo.push('さん')
        return contributorInfo.join(' ')
    }
}

export function getThreadContributorLabelFromSession(): string {

    let contributorLabel: string = ''
    const boardUserDisplay: string = SessionStorageHelper.getStringItemWithDefault(
        'selectedCondominiumBoardUserDisplay', '')
    const userId: string = SessionStorageHelper.getStringItemWithDefault('userId', '')
    const selectedRidgeName: string = SessionStorageHelper.getStringItemWithDefault('selectedRidgeName', '')
    const selectedRoomNumber: string = SessionStorageHelper.getStringItemWithDefault('selectedRoomNumber', '')
    const loginFamilyName: string = SessionStorageHelper.getStringItemWithDefault('loginFamilyName', '')
    const loginFirstName: string = SessionStorageHelper.getStringItemWithDefault('loginFirstName', '')
    const loginRoleName: string = SessionStorageHelper.getStringItemWithDefault('loginRoleName', '')

    if (!boardUserDisplay) {
        return contributorLabel
    }

    if (userId.slice(0, 1) === ROLE_TYPE.ADMIN.value) {
        contributorLabel = loginRoleName
        return contributorLabel
    }

    switch (boardUserDisplay) {
        case USER_DISPLAY_ITEMS.BOTH.value:
            contributorLabel = `${selectedRidgeName} ${selectedRoomNumber} ${loginFamilyName} ${loginFirstName} さん`
            break
        case USER_DISPLAY_ITEMS.ROOM.value:
            contributorLabel = `${selectedRidgeName} ${selectedRoomNumber} さん`
            break
        case USER_DISPLAY_ITEMS.NAME.value:
            contributorLabel = `${loginFamilyName} ${loginFirstName} さん`
            break
        case USER_DISPLAY_ITEMS.NONE.value:
            contributorLabel = '匿名 さん'
            break
    }

    return contributorLabel
}

export function downloadDataUriSchemeOnMsSaveBlob(fileDataUriScheme: string, fileName: string): void {
    if (window.navigator.msSaveBlob) {
        const fileData = fileDataUriScheme.split(',')[1]
        const fileDataInfo = fileDataUriScheme.split(',')[0]
        const binaryData: string = atob(fileData)
        const tmpFileDataInfo: string[] = fileDataInfo.split(':')
        const mimeType: string = tmpFileDataInfo[1].split(';')[0]
        const blobFileData = new Uint8Array(binaryData.length)

        for (let i = 0; binaryData.length > i; i++) {
            blobFileData[i] = binaryData.charCodeAt(i)
        }

        const blob = new Blob([blobFileData], {type: mimeType})
        window.navigator.msSaveBlob(blob, fileName)
    }
}

declare global {
    interface Navigator {
        msSaveBlob?: (blob: any, defaultName?: string) => boolean;
    }
}

export function getPrefectureName(prefectureCd: string): string {
    const prefecturesMaster = PREFECTURES_MASTER.codeToEnumConstants(prefectureCd)
    if (prefecturesMaster) {
        return prefecturesMaster.label
    } else {
        return ''
    }
}

export function setSessionStorage(key: string, value: any | null): void {
    if (value) {
        SessionStorageHelper.setItem(key, value)
    } else {
        SessionStorageHelper.setItem(key, '')
    }
}

export function getFormattedZipCode(zipCode: string, needZipCodeMark: boolean = true): string {
    if (!zipCode) {
        return ''
    }
    if (zipCode.length !== 7) {
        return zipCode
    }
    const formatZipCode = zipCode.substr(0, 3) + '-' + zipCode.substr(-4, 4)
    return (needZipCodeMark) ? '〒' + formatZipCode : formatZipCode
}

export function toFullWidthNumber(halfWidthNumber: number): string {
    return halfWidthNumber.toString().replace(/[0-9]/g, (half) => {
        return String.fromCharCode(half.charCodeAt(0) + 0xFEE0)
    })
}

export function toHalfWidthNumber(fullWidthNumber: string): string {
    return fullWidthNumber.toString().replace(/[０-９]/g, (full) => {
        return String.fromCharCode(full.charCodeAt(0) - 0xFEE0)
    })
}

export function cloneBean<T>(bean: T): T {
    return JSON.parse(JSON.stringify(bean))
}

export function getRandomKey(): string {
    return `${Math.random().toString().substring(2)}_${new Date().getTime()}`
}

export const getFormattedAddress = (
    zipcode: string,
    prefecture: string,
    address2: string,
    address3: string,
    buildingRoomNumber: string,
): string => {
    const address = []
    if (zipcode) {
        address.push(getFormattedZipCode(zipcode))
    }
    address.push(`${prefecture ?? ''}${address2 ?? ''}${address3 ?? ''}${buildingRoomNumber ?? ''}`)

    return address.join('　')
}

export const autoKanaOnUpdate = (
    input: string,
    onKanaChange: (value: string) => void,
) => {
    if (input && input.split('').every(char => !isKanji(char))) {
        const currentHiragana = input.split('').map(char => isKana(char) ? char : '').join('')
        onKanaChange(toKatakana(currentHiragana))
    }
}
